import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTestToTake, submitTest, getMyExamReview } from '../utils/api';

/* Taking a University test that an admin unlocked.
   • CPR Field Rep Exam — every question on one page by section, Submit, then the score,
     section breakdown and what you missed (same as the original exam page).
   • Closer Profile Assessment — one statement per screen, then 10 "which would mean more" choices.
     On submit the person only sees "Thank you". The profile goes to the managers only.
   Questions and wording come from the server exactly as written in the original tests.       */

const L = 'ABCD';
const pct = (a, b) => (b ? Math.round(a / b * 100) : 0);

export const TEST_CSS = `
.tt{--bg:#f2f5f2;--surface:#fff;--fg:#16211b;--muted:#5a685f;--line:#d8e0da;--accent:#1f5b3a;--accent-soft:#e2efe7;--gold:#a8801f;--good:#1f7a4a;--good-soft:#e2f2e9;--bad:#b3362c;--bad-soft:#f9e5e3;
  --or:#e2621b;color:var(--fg);max-width:680px;margin:0 auto;display:flex;flex-direction:column;gap:16px;font-size:16px;line-height:1.5}
.tt h1{font-size:1.9rem;line-height:1.1;margin:0;font-weight:800}
.tt .sub{margin:0;color:var(--muted);font-size:.95rem}
.tt-bar{position:sticky;top:0;z-index:5;background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:10px 14px;display:flex;align-items:center;gap:12px}
.tt-track{flex:1;height:6px;background:var(--line);border-radius:3px;overflow:hidden}
.tt-fill{height:100%;background:var(--gold);transition:width .2s}
.tt-count{font-size:.85rem;color:var(--muted);white-space:nowrap;font-variant-numeric:tabular-nums}
.tt h2{font-size:1.15rem;text-transform:uppercase;letter-spacing:.04em;color:var(--accent);margin:18px 0 0;display:flex;justify-content:space-between;gap:12px;align-items:baseline}
.tt h2 small{font-size:.8rem;font-weight:500;color:var(--muted);text-transform:none;letter-spacing:0;white-space:nowrap}
.tt-q{background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:16px;display:flex;flex-direction:column;gap:10px}
.tt-q.missing{border-color:var(--bad)}
.tt-q h3{font-size:1rem;font-weight:600;margin:0}
.tt-opts{display:flex;flex-direction:column;gap:8px}
.tt-opt{display:flex;gap:10px;align-items:flex-start;text-align:left;width:100%;font:inherit;color:var(--fg);background:transparent;border:1px solid var(--line);border-radius:8px;padding:10px 12px;cursor:pointer}
.tt-opt:hover{border-color:var(--accent)}
.tt-opt b{min-width:1.2em}
.tt-opt.sel{background:var(--accent-soft);border-color:var(--accent)}
.tt-btn{font:600 1rem inherit;font-family:inherit;border:1px solid var(--accent);border-radius:8px;padding:12px 18px;background:var(--accent);color:#fff;cursor:pointer}
.tt-btn.ghost{background:transparent;color:var(--accent)}
.tt-btn:disabled{opacity:.5;cursor:default}
.tt-warn{color:var(--bad);font-size:.92rem;margin:0}
.tt-res{background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:20px;display:flex;flex-direction:column;gap:6px}
.tt-score{font-size:3rem;font-weight:800;line-height:1;font-variant-numeric:tabular-nums}
.tt-pill{align-self:flex-start;font-size:.8rem;font-weight:700;letter-spacing:.05em;text-transform:uppercase;padding:4px 10px;border-radius:99px}
.tt-pill.pass{background:var(--good-soft);color:var(--good)}.tt-pill.fail{background:var(--bad-soft);color:var(--bad)}
.tt-secs{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:8px;margin-top:10px}
.tt-sec{border:1px solid var(--line);border-radius:8px;padding:8px 10px;font-size:.82rem;color:var(--muted)}
.tt-sec strong{display:block;font-size:1.1rem}
.tt-sec.ok strong{color:var(--good)}.tt-sec.low strong{color:var(--bad)}
.tt-miss{background:var(--surface);border:1px solid var(--line);border-left:4px solid var(--bad);border-radius:8px;padding:14px 16px;display:flex;flex-direction:column;gap:6px}
.tt-miss h3{margin:0;font-size:.98rem;font-weight:600}
.tt-miss p{margin:0;font-size:.95rem}
.tt-tag{font-size:.75rem;text-transform:uppercase;letter-spacing:.05em;color:var(--muted)}
.tt-yours{color:var(--bad)}.tt-right{color:var(--good)}
.tt-panel{background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:20px;display:flex;flex-direction:column;gap:14px}
.tt-eyebrow{font-weight:800;letter-spacing:.12em;text-transform:uppercase;font-size:13px;color:var(--or)}
.tt-pq{font-size:23px;font-weight:600;line-height:1.3;min-height:3.9em;margin:0}
.tt-lk{display:flex;justify-content:space-between;gap:10px;align-items:center;padding:13px 16px;border-radius:10px;border:1.5px solid var(--line);background:var(--surface);font:inherit;cursor:pointer;text-align:left}
.tt-lk:hover{border-color:var(--or)}
.tt-lk.sel{border-color:var(--or);box-shadow:inset 4px 0 0 var(--or)}
.tt-lk small{color:var(--muted)}
.tt-vs{text-align:center;font-weight:800;color:var(--muted);letter-spacing:.1em}
.tt-top{display:flex;justify-content:space-between;align-items:center;gap:12px}
.tt-or .tt-fill{background:var(--or)}
.tt-done{text-align:center;padding:40px 20px}
.tt-done .big{font-size:52px}
`;

/* ───────── FIELD REP EXAM ───────── */
function Exam({ t, onDone }) {
  const items = useMemo(() => t.sections.flatMap((s, k) => s.questions.map(q => ({ k, q }))), [t]);
  const [picked, setPicked] = useState(() => Array(items.length).fill(null));
  const [warn, setWarn] = useState('');
  const [missing, setMissing] = useState([]);
  const [busy, setBusy] = useState(false);
  const answered = picked.filter(a => a !== null).length;

  async function submit() {
    const blank = picked.map((a, i) => (a === null ? i : -1)).filter(i => i >= 0);
    if (blank.length) {
      setMissing(blank); setWarn(`Answer every question first. Still blank: ${blank.map(i => i + 1).join(', ')}.`);
      document.getElementById('tq' + blank[0])?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setBusy(true);
    try { onDone(await submitTest(t.key, { answers: picked })); }
    catch (e) { setWarn(e.response?.data?.error || 'Could not submit. Check your connection and try again.'); }
    finally { setBusy(false); }
  }

  let i = 0;
  return (
    <>
      <div>
        <h1>{t.title}</h1>
        <p className="sub">{items.length} questions in {t.sections.length} sections · Pass = {t.pass} correct ({pct(t.pass, items.length)}%) · Tap one answer per question, then Submit.
          {t.left === 1 ? ' This is your last try.' : ` You have ${t.left} tries.`}</p>
      </div>
      <div className="tt-bar"><div className="tt-track"><div className="tt-fill" style={{ width: pct(answered, items.length) + '%' }} /></div><span className="tt-count">{answered} / {items.length} answered</span></div>
      {t.sections.map((s, k) => (
        <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2><span>{k + 1}. {s.name}</span><small>{s.questions.length} questions</small></h2>
          {s.questions.map(q => {
            const idx = i++;
            return (
              <div key={idx} id={'tq' + idx} className={'tt-q' + (missing.includes(idx) && picked[idx] === null ? ' missing' : '')}>
                <h3>{idx + 1}. {q.q}</h3>
                <div className="tt-opts" role="group" aria-label={`Question ${idx + 1}`}>
                  {q.o.map((o, j) => (
                    <button key={j} type="button" className={'tt-opt' + (picked[idx] === j ? ' sel' : '')} aria-pressed={picked[idx] === j}
                      onClick={() => setPicked(p => p.map((v, x) => (x === idx ? j : v)))}>
                      <b>{L[j]}.</b><span>{o}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {warn && <p className="tt-warn">{warn}</p>}
      <div><button className="tt-btn" type="button" onClick={submit} disabled={busy}>{busy ? 'Submitting…' : 'Submit test'}</button></div>
    </>
  );
}

export function ExamResults({ r, title, onBack, left, backLabel = 'Back to University' }) {
  return (
    <>
      <div><h1>{title}</h1><p className="sub">{r.total} questions · Pass = {r.pass} correct ({pct(r.pass, r.total)}%)</p></div>
      <div className="tt-res">
        <span className={'tt-pill ' + (r.passed ? 'pass' : 'fail')}>{r.passed ? 'Passed' : 'Not passed'}</span>
        <div className="tt-score">{r.score} / {r.total}</div>
        <p style={{ margin: 0, color: 'var(--muted)' }}>{pct(r.score, r.total)}% · passing is {r.pass} ({pct(r.pass, r.total)}%)</p>
        <div className="tt-secs">{r.sections.map(s => (
          <div key={s.name} className={'tt-sec ' + (s.right / s.total >= 0.8 ? 'ok' : 'low')}><strong>{s.right} / {s.total}</strong>{s.name}</div>
        ))}</div>
      </div>
      <h2><span>{r.missed.length ? 'What you missed' : 'Perfect score'}</span>{r.missed.length > 0 && <small>{r.missed.length} questions</small>}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {r.missed.map(m => (
          <div key={m.n} className="tt-miss">
            <span className="tt-tag">{m.section}</span>
            <h3>{m.n}. {m.q}</h3>
            <p className="tt-yours">Your answer: {L[m.yours]}. {m.yoursText}</p>
            <p className="tt-right">Correct: {L[m.correct]}. {m.correctText}</p>
          </div>
        ))}
      </div>
      {left != null && <p className="sub">{left > 0 ? `You have ${left} more ${left === 1 ? 'try' : 'tries'}. Study what you missed, then try again from University → Tests.` : 'That was your last try. If you need another, ask your manager.'}</p>}
      <div><button className="tt-btn ghost" type="button" onClick={onBack}>{backLabel}</button></div>
    </>
  );
}

/* ───────── CLOSER PROFILE ───────── */
function makePairs(keys) {   // every pair once, random sides and random order (same as the original)
  const P = [];
  for (let i = 0; i < keys.length; i++) for (let j = i + 1; j < keys.length; j++) P.push(Math.random() < 0.5 ? [keys[i], keys[j]] : [keys[j], keys[i]]);
  for (let i = P.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [P[i], P[j]] = [P[j], P[i]]; }
  return P;
}

function Profile({ t, onDone }) {
  const nL = t.items.length;
  const pairs = useMemo(() => makePairs(Object.keys(t.motivators)), [t]);
  const TOTAL = nL + pairs.length;
  const [i, setI] = useState(-1);
  const [lik, setLik] = useState(() => Array(nL).fill(null));
  const [mot, setMot] = useState(() => Array(pairs.length).fill(null));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, [i]);

  async function finish(m) {
    setBusy(true); setErr('');
    try {
      await submitTest(t.key, { likert: lik, pairs: pairs.map(([a, b], k) => ({ a, b, pick: m[k] })) });
      onDone();
    } catch (e) { setErr(e.response?.data?.error || 'Could not submit. Check your connection and press Submit again.'); }
    finally { setBusy(false); }
  }
  const choose = (setter, v) => { setter(v); setTimeout(() => setI(x => x + 1), 170); };

  if (i < 0) return (
    <>
      <div className="tt-eyebrow">Sales hiring · Profile</div>
      <h1 style={{ textTransform: 'uppercase' }}>{t.title}</h1>
      <p className="sub">This isn't a pass-or-fail test. It shows how you think, what drives you, and how you'll learn sales fastest. Answer how you actually are, not how you think a salesperson should be.</p>
      <div className="tt-panel">
        <strong style={{ textTransform: 'uppercase', letterSpacing: '.07em' }}>Two parts · about 12 minutes</strong>
        <p style={{ margin: 0 }}><b style={{ color: 'var(--or)' }}>1</b>&nbsp; <strong>{nL} statements.</strong> Rate how much each one sounds like you.</p>
        <p style={{ margin: 0 }}><b style={{ color: 'var(--or)' }}>2</b>&nbsp; <strong>10 quick choices.</strong> Pick which of two things matters more to you.</p>
        <p className="sub" style={{ fontSize: 14 }}>Go with your first instinct. Don't overthink it. You can take this once.</p>
      </div>
      <button className="tt-btn" style={{ background: 'var(--or)', borderColor: 'var(--or)', textTransform: 'uppercase', letterSpacing: '.06em' }} onClick={() => setI(0)}>Start</button>
    </>
  );

  const head = part => (
    <>
      <div className="tt-top">
        <button className="tt-btn ghost" style={{ padding: '8px 14px' }} onClick={() => setI(x => x - 1)}>← Back</button>
        <span className="tt-count" style={{ textAlign: 'right', fontWeight: 700 }}>{part}<br />{Math.min(i + 1, TOTAL)} / {TOTAL}</span>
      </div>
      <div className="tt-track tt-or"><div className="tt-fill" style={{ width: (i / TOTAL * 100) + '%' }} /></div>
    </>
  );

  if (i < nL) return (
    <>
      {head('Part 1')}
      <p className="sub">How much does this sound like you?</p>
      <p className="tt-pq">{t.items[i]}</p>
      <div className="tt-opts">
        {t.scale.map((l, v) => (
          <button key={v} className={'tt-lk' + (lik[i] === v + 1 ? ' sel' : '')} onClick={() => choose(() => setLik(a => a.map((x, k) => (k === i ? v + 1 : x))))}>
            <span>{l}</span><small>{v + 1}</small>
          </button>
        ))}
      </div>
    </>
  );

  if (i < TOTAL) {
    const j = i - nL, [a, b] = pairs[j];
    return (
      <>
        {head('Part 2')}
        <p className="sub">Which would mean more to you?</p>
        <div className="tt-opts">
          <button className={'tt-lk' + (mot[j] === a ? ' sel' : '')} onClick={() => choose(() => setMot(m => m.map((x, k) => (k === j ? a : x))))}>{t.motivators[a]}</button>
          <div className="tt-vs">OR</div>
          <button className={'tt-lk' + (mot[j] === b ? ' sel' : '')} onClick={() => choose(() => setMot(m => m.map((x, k) => (k === j ? b : x))))}>{t.motivators[b]}</button>
        </div>
      </>
    );
  }

  // review screen: everything answered? then submit
  const blankL = lik.map((v, k) => (v == null ? k : -1)).filter(k => k >= 0);
  const blankM = mot.map((v, k) => (v == null ? k : -1)).filter(k => k >= 0);
  return (
    <>
      <div className="tt-track tt-or"><div className="tt-fill" style={{ width: '100%' }} /></div>
      <div className="tt-panel">
        <h1 style={{ fontSize: '1.6rem' }}>All done</h1>
        {blankL.length || blankM.length
          ? <p className="tt-warn">A few answers are missing. <button className="tt-btn ghost" style={{ padding: '4px 10px', marginLeft: 6 }} onClick={() => setI(blankL.length ? blankL[0] : nL + blankM[0])}>Go to the first one</button></p>
          : <p className="sub">Press Submit to send your answers. You can't change them after this.</p>}
        {err && <p className="tt-warn">{err}</p>}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="tt-btn ghost" onClick={() => setI(TOTAL - 1)}>← Back</button>
          <button className="tt-btn" style={{ background: 'var(--or)', borderColor: 'var(--or)' }} disabled={busy || blankL.length > 0 || blankM.length > 0} onClick={() => finish(mot)}>{busy ? 'Submitting…' : 'Submit'}</button>
        </div>
      </div>
    </>
  );
}

/* ───────── PAGE ───────── */
export default function TakeTest() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);     // exam result, or 'thanks' for the profile
  const back = () => navigate('/training?tab=tests');

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('review') === '1' && key === 'field-rep-exam') {
      getMyExamReview().then(r => { setT({ title: 'CPR Field Rep Exam', key }); setResult({ ...r, left: undefined, review: true }); })
        .catch(() => setErr('No exam result yet.'));
      return;
    }
    getTestToTake(key).then(setT).catch(e => setErr(e.response?.data?.error || 'This test is not available.'));
  }, [key]);

  return (
    <div className="page-body">
      <style>{TEST_CSS}</style>
      <div className="tt">
        {err ? (
          <div className="tt-panel"><h1 style={{ fontSize: '1.5rem' }}>Not available</h1><p className="sub">{err}</p><div><button className="tt-btn ghost" onClick={back}>Back to University</button></div></div>
        ) : !t ? <div className="loader"><div className="spinner" /></div>
          : result === 'thanks' ? (
            <div className="tt-panel tt-done">
              <div className="big">✅</div>
              <h1 style={{ fontSize: '1.7rem' }}>Thank you, submitted</h1>
              <p className="sub">Your answers have been sent to your manager. There's nothing else you need to do.</p>
              <div><button className="tt-btn" onClick={back}>Back to University</button></div>
            </div>
          ) : result ? (
            <ExamResults r={result} title={t.title} onBack={back} left={result.review ? null : result.left} />
          ) : t.kind === 'exam' ? (
            <Exam t={t} onDone={r => { setResult(r); window.scrollTo(0, 0); }} />
          ) : (
            <Profile t={t} onDone={() => setResult('thanks')} />
          )}
      </div>
    </div>
  );
}
